import { Stack } from "@mui/material";
import type { FC } from "react";
import { usePortfolioDocumentsQuery } from "web_myplann/Api/Portfolio/Queries/usePortfolioDocumentsQuery";
import type { PropertyInsuranceDetailDataDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { DetailSectionLoader } from "web_myplann/Portfolio/Components/DetailSectionLoader";
import {
	BasicInfo,
	hasBasicInfo,
} from "web_myplann/Portfolio/Components/PortfolioSections/BasicInfo";
import { RelatedSection } from "web_myplann/Portfolio/Components/RelatedSection";
import { computeRelatedPresence } from "web_myplann/Portfolio/Helpers/getHasRelated";
import { DataCardPreset } from "web_myplann/Shared/Components/DataCard";
import {
	DataRowCard,
	type DataRowCardItem,
} from "web_myplann/Shared/Components/DataRowCard";
import { moneyFormatter } from "web_myplann/Shared/Utils/moneyFormatters";
import type { PortfolioDetailProps } from "web_myplann/Utils/types";

const PageResources = Resources.PortfolioDetail.NonLifeInsurance;

export const PropertyInsurance: FC<PortfolioDetailProps> = ({
	baseData,
	currencySign,
}) => {
	const { t } = useResource();

	const { data: documents, isLoading: isDocumentsLoading } =
		usePortfolioDocumentsQuery(baseData.id);

	if (isDocumentsLoading) {
		return <DetailSectionLoader />;
	}

	const portfolioData = baseData.data as PropertyInsuranceDetailDataDTO;

	const basicInfoItems: DataRowCardItem[] = [
		{ label: t(PageResources.name), value: baseData.clientName },
		{
			label: t(PageResources.contractNumber),
			value: baseData?.contractNumber,
		},
		{ label: t(PageResources.producent), value: baseData?.producer },
		{ label: t(PageResources.product), value: baseData?.product },
		{
			label: t(PageResources.insured),
			value: moneyFormatter(portfolioData?.insurance, currencySign),
		},
		{
			label: t(PageResources.insurancePeriod),
			value: moneyFormatter(portfolioData?.insurancePeriod, currencySign),
		},
	];

	return (
		<Stack>
			<DataRowCard
				preset={DataCardPreset.BasicInfo}
				bottomComponent={
					hasBasicInfo(baseData) ? <BasicInfo data={baseData} /> : null
				}
				noDivider={
					!computeRelatedPresence(documents?.data, baseData.related).hasAny
				}
				items={basicInfoItems}
			/>

			<RelatedSection
				documents={documents?.data}
				related={baseData.related}
				portfolioId={baseData.id}
			/>
		</Stack>
	);
};
